import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  useTopics,
  useCreateTopic,
  useUpdateTopic,
  useDeleteTopic,
} from '@/api/queries/promptTopics';
import type { PromptTopic, CreatePromptTopicDto, UpdatePromptTopicDto } from '@/types/prompt';

interface TopicManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EditingTopic {
  id: string;
  name: string;
  description: string;
}

export function TopicManagementDialog({ open, onOpenChange }: TopicManagementDialogProps) {
  const { t } = useTranslation('prompts');
  const { t: tCommon } = useTranslation('common');
  const [editingTopic, setEditingTopic] = useState<EditingTopic | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTopic, setNewTopic] = useState<CreatePromptTopicDto>({
    name: '',
    description: '',
  });

  const { data: topicsData, isLoading } = useTopics();
  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();
  const deleteTopic = useDeleteTopic();

  const handleCreateTopic = () => {
    if (!newTopic.name.trim() || !newTopic.description.trim()) return;

    const topicData: CreatePromptTopicDto = {
      name: newTopic.name.trim(),
      description: newTopic.description.trim(),
    };

    createTopic.mutate(topicData, {
      onSuccess: () => {
        setNewTopic({ name: '', description: '' });
        setShowCreateForm(false);
      },
    });
  };

  const handleUpdateTopic = () => {
    if (!editingTopic || !editingTopic.name.trim() || !editingTopic.description.trim()) return;

    const updateData: UpdatePromptTopicDto = {
      name: editingTopic.name.trim(),
      description: editingTopic.description.trim(),
    };

    updateTopic.mutate(
      {
        topicObjectId: editingTopic.id,
        data: updateData,
      },
      {
        onSuccess: () => {
          setEditingTopic(null);
        },
      }
    );
  };

  const handleDeleteTopic = (topicId: string, topicName: string) => {
    if (window.confirm(t('topics.confirmDelete', { name: topicName }))) {
      deleteTopic.mutate(topicId);
    }
  };

  const startEditing = (topic: PromptTopic) => {
    setEditingTopic({
      id: topic._id!,
      name: topic.name,
      description: topic.description,
    });
  };

  const cancelEditing = () => {
    setEditingTopic(null);
  };

  const cancelCreate = () => {
    setNewTopic({ name: '', description: '' });
    setShowCreateForm(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-2xl max-h-[80vh] !flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('topics.manageTopics')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Create New Topic Section */}
          <div className="mb-6">
            {!showCreateForm ? (
              <Button
                onClick={() => setShowCreateForm(true)}
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('topics.createNewTopic')}
              </Button>
            ) : (
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="new-topic-name">{t('name')}</Label>
                      <Input
                        id="new-topic-name"
                        value={newTopic.name}
                        onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
                        placeholder={t('topics.placeholders.topicName')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-topic-description">{t('description')}</Label>
                      <Textarea
                        id="new-topic-description"
                        value={newTopic.description}
                        onChange={e => setNewTopic({ ...newTopic, description: e.target.value })}
                        placeholder={t('topics.placeholders.topicDescription')}
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleCreateTopic}
                        disabled={
                          !newTopic.name.trim() ||
                          !newTopic.description.trim() ||
                          createTopic.isPending
                        }
                        size="sm"
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {createTopic.isPending
                          ? tCommon('buttons.saving')
                          : t('topics.createTopic')}
                      </Button>
                      <Button onClick={cancelCreate} variant="outline" size="sm">
                        <X className="h-4 w-4 mr-1" />
                        {tCommon('buttons.cancel')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Topics List */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {isLoading ? (
              <div className="text-center py-8">{t('topics.loadingTopics')}</div>
            ) : (
              <>
                {topicsData?.topics.map((topic: PromptTopic) => (
                  <Card key={topic._id}>
                    <CardContent className="pt-4">
                      {editingTopic && editingTopic.id === topic._id ? (
                        // Edit Mode
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor={`edit-name-${topic._id}`}>{t('name')}</Label>
                            <Input
                              id={`edit-name-${topic._id}`}
                              value={editingTopic.name}
                              onChange={e =>
                                setEditingTopic({ ...editingTopic, name: e.target.value })
                              }
                            />
                          </div>
                          <div>
                            <Label htmlFor={`edit-description-${topic._id}`}>
                              {t('description')}
                            </Label>
                            <Textarea
                              id={`edit-description-${topic._id}`}
                              value={editingTopic.description}
                              onChange={e =>
                                setEditingTopic({ ...editingTopic, description: e.target.value })
                              }
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={handleUpdateTopic}
                              disabled={
                                !editingTopic.name.trim() ||
                                !editingTopic.description.trim() ||
                                updateTopic.isPending
                              }
                              size="sm"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              {updateTopic.isPending
                                ? tCommon('buttons.saving')
                                : t('topics.saveTopic')}
                            </Button>
                            <Button onClick={cancelEditing} variant="outline" size="sm">
                              <X className="h-4 w-4 mr-1" />
                              {tCommon('buttons.cancel')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{topic.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
                            <div className="text-xs text-gray-500 mt-2">
                              {t('labels.created')}:{' '}
                              {new Date(topic.metadata.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-4">
                            <Button variant="ghost" size="sm" onClick={() => startEditing(topic)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTopic(topic._id!, topic.name)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {topicsData?.topics.length === 0 && (
                  <div className="text-center py-8 text-gray-500">{t('topics.noTopicsFound')}</div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => onOpenChange(false)} variant="outline">
              {tCommon('buttons.close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
